import React, { useState, useRef, useEffect } from "react"
import sanitizeHtml               from 'sanitize-html-react'
import { library }                from '@fortawesome/fontawesome-svg-core'
import { fas }                    from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon }        from '@fortawesome/react-fontawesome'
import { getRequest, pathname }   from '@react/helpers/fetch'

library.add(fas)
/*
  <Combobox
    -----------
    EXAMPLE FOR identifier/display
    Props passed               -> identifier: 'id', display: 'name'
    Expected response format   -> { id: 2, name: 'Miguel' }
    -----------

    identifier:      String. Unique identifier between items. Usually 'id'
    display:         String. What part of the hash will be displayed
    options:         Array.  Contains all the suggestions [{ id: 2, name: 'Miguel' }, ...]
    label:           String. Render a label with given string
    className:       String. Optional css class to give to the container
    value:           Number. Default selected option identifier
    disabled:        Boolean
    inputClassName:  String. Optional css class to give to the input
    onSelectElement: Function. Trigger something everytume we select/unselect someone
    required:        Boolean. required input
    feed:            URL. If given, it will work as an autocomplete
    -----------------
  />
*/
// Fold a string for matching: case and accent insensitive ("Pêche" -> "peche").
// Character by character so the folded string keeps the same length as the
// original (needed to map match positions back when highlighting).
const foldChar = char => {
  const folded = char.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  return folded.length === char.length ? folded : char
}
const fold     = string => Array.from(`${string}`, foldChar).join('')
const tokens   = query => fold(query || '').split(/\s+/).filter(Boolean)

// Every word of the query must appear somewhere in the target, in any order:
// "pink belvedere" matches "Belvédère Lac Pink".
export const hit = (target, query) => {
  const haystack = fold(target)
  return tokens(query).every(token => haystack.includes(token))
}

// Bold every query token inside the suggestion, preserving the original text.
const highlight = (text, query) => {
  const saneText   = sanitizeHtml(text)
  const queryTokens = tokens(query)
  if (queryTokens.length === 0) return { __html: saneText }

  const folded = fold(saneText)
  const bold   = new Array(saneText.length).fill(false)
  queryTokens.forEach(token => {
    let index = folded.indexOf(token)
    while (index !== -1) {
      for (let i = index; i < index + token.length; i++) bold[i] = true
      index = folded.indexOf(token, index + token.length)
    }
  })

  let html = ''
  for (let i = 0; i < saneText.length; i++) {
    if (bold[i] && (i === 0 || !bold[i - 1]))  html += '<b>'
    html += saneText[i]
    if (bold[i] && (i === saneText.length - 1 || !bold[i + 1])) html += '</b>'
  }
  return { __html: html }
}

const Combobox = ({
    identifier = 'id',
    display    = 'name',
    options    = [],
    data       = {},
    inputStyle = {},
    autocomplete = false,
    disabled     = false,
    multi        = false,
    activated    = false,
    resetIcon    = false,
    label,
    className,
    value,
    fieldName,
    onSelectElement,
    onClear,
    onReset,
    prompt,
    placeholder,
    required,
    feed
  }) => {
  const inputRef       = useRef(null)
  const hiddenInputRef = useRef(null)
  const suggestionsRef = useRef(null)
  const timeoutRef     = useRef(null)
  const comboRef       = useRef(null)

  const didSelect      = useRef(false)
  const didMountRef    = useRef(false)
  // Typing opens the list: skip the on-open reset so the keystroke is kept
  const skipResetRef   = useRef(false)

  const promptOption = prompt ? { [display] : prompt  } : {}
  const preOptions   = prompt && !required ? [promptOption, ...options] : options
  const preselected  = value ? options.find(option => option[identifier] == value) || {} : {}

  // Data attributes for the hidden input, makes the component stimulus compatible
  const inputData  = {}
  Object.keys(data).forEach(key =>{
    const dataKey = "data-" + key.toString()
    inputData[dataKey] = data[key]
  })

  const [state, setState] = useState({
    // Current option list
    suggestions: preOptions,
    // The actual selected option
    selected:    preselected,
    // The selection while moving arrows
    highlighted: preselected,
    // Indicates if the list is shown or not
    active:      activated
  })

  const [search, setSearch] = useState(preselected[display])
  const {suggestions, selected, highlighted, active} = state

  const highlightedIndex = suggestions.findIndex(suggestion =>
    suggestion[identifier] == highlighted[identifier]
  )

  const onSearch = query => {
    if (query.length === 0) {
      resetInput()
      return
    }

    if (!active) {
      skipResetRef.current = true
      activate()
    }

    setSearch(query)
    if (feed) {
      clearTimeout(timeoutRef.current)
      const url = pathname({ path: feed, params: { q: query }})
      timeoutRef.current = setTimeout(() => {
        getRequest(url).then(response => {
          setState(prev => ({...prev,
            active:      true,
            suggestions: response,
            highlighted: response[0] || {}
          }))
        })
      }, 500)
    } else {
      const hits = options.filter(option => hit(option[display], query))
      setState(prev => ({...prev,
        active:      true,
        suggestions: hits,
        highlighted: hits[0] || {}
      }))
    }
  }

  const onKeyDown = e => {
    let selection
    switch(e.key) {
      case "ArrowUp":
        selection = suggestions[highlightedIndex - 1] || {}
        setState({...state, highlighted: selection})
        break
      case "ArrowDown":
        selection = suggestions[highlightedIndex + 1] || {}
        setState({...state, highlighted: selection})
        break
      case "Backspace":
        if (onClear && search == undefined) {
          deactivate()
          onClear()
        }
        break
      case "Enter":
        if (active) {
          e.preventDefault()
          e.stopPropagation()
          selectItem(highlighted)
        }
        break
      case "Escape":
        if (active) {
          // Close the list without closing the surrounding modal
          e.preventDefault()
          e.stopPropagation()
          deactivate()
        }
        break
    }
  }

  const toggle     = () => active ? deactivate() : activate()
  const deactivate = () => setState({...state, active: false})
  const activate   = () => setState({...state, active: true})

  const clickItem = (e, selected) => {
    e.preventDefault()
    e.stopPropagation()

    selectItem(selected)
  }

  const selectItem = selected => {
    if (!selected) return

    if (multi) {
      setState({...state, active: false })
      setSearch(undefined)
    } else {
      setState({
        selected:    selected,
        highlighted: selected,
        suggestions: feed ? [selected] : preOptions,
        active:      false
      })

      setSearch(selected[display])
    }
    if (onSelectElement) onSelectElement(selected)
    didSelect.current = true
  }

  const reset = () => {
    setState({
      suggestions: preOptions,
      selected:    {},
      highlighted: {},
      active:      false
    })
    setSearch(undefined)
    if (onReset) onReset()
  }

  const resetInput = () => {
    setState({
      ...state,
      suggestions: preOptions
    })
    setSearch(undefined)
  }

  const handleBlur = () => {
    const isValidSelection = selected[identifier] && search === selected[display]
    
    if (!isValidSelection) {
      setSearch(selected[identifier] ? selected[display] : undefined)
    }
  }

  useEffect(() => {
    if (didMountRef.current) {
      if (active) {
        inputRef.current.focus()
        if (skipResetRef.current) {
          skipResetRef.current = false
        } else {
          resetInput()
        }
      } else {
        const selectedValue = selected[identifier] ? selected[display] : undefined
        if (!autocomplete) setSearch(selectedValue)
      }
    }
  }, [active])

  useEffect(() => {
    if (didSelect.current && data && fieldName && hiddenInputRef.current) {
      const event = new Event("change")
      hiddenInputRef.current.dispatchEvent(event)
    }
  }, [selected])

  useEffect(() => {
    if (didMountRef.current) {
      setState({...state, selected: preselected})
      setSearch(preselected[display])
    }
  }, [value])

  useEffect(() => {
    if (didMountRef.current && activated && !active) {
      activate()
    }
  }, [activated])

  const closeSuggestions = e => {
    if(comboRef.current && suggestionsRef.current && !comboRef.current.contains(e.target)) {
      deactivate()
    }
  }

  useEffect(() => {
    didMountRef.current = true
    document.addEventListener('mousedown', closeSuggestions)

    // Allow external code (e.g. Stimulus controllers) to update the selected value
    if (hiddenInputRef.current) {
      const handleExternalUpdate = (e) => {
        const newValue = e.detail
        if (newValue && newValue[identifier]) {
          setState(prev => ({
            ...prev,
            selected:    newValue,
            suggestions: [newValue],
            active:      false
          }))
          setSearch(newValue[display])
        }
      }
      hiddenInputRef.current.addEventListener('combobox:update', handleExternalUpdate)
    }
  }, [])

  const scrollHighlighted = () => {
    if (active) {
      const list = suggestionsRef.current
      if (list) {
        const item = list.querySelectorAll('li')[highlightedIndex]
        if(item) list.scrollTop = item.offsetTop
      }
    }
  }

  scrollHighlighted()

  return (
    <>
      <div className = {`autocomplete__container ${className || ""}`} ref={comboRef}>
        {/* Optional label */}
        { label &&
          <div className="input-group">
            <label required={required}>{label}</label>
          </div>
        }
        <div className="autocomplete__input-container">
          {/* Input for autocomplete */}
          <input
            type         = "text"
            ref          = {inputRef}
            className    = {'autocomplete__input pointer'}
            value        = {sanitizeHtml(search || '')}
            name         = {autocomplete ? fieldName : ''}
            disabled     = {disabled}
            onFocus      = {activate}
            onBlur       = {handleBlur}
            onChange     = {e => onSearch(e.target.value)}
            onKeyDown    = {onKeyDown}
            placeholder  = {active ? I18n.t("navigation.search_placeholder") : (placeholder || prompt || selected[display])}
            required     = {required}
            style        = {{ ...inputStyle, paddingRight: '44px' }}
            autoFocus    = {active}
            autoComplete = "off"
          />

          { fieldName && !multi && !autocomplete &&
            <input
              hidden
              ref              = {hiddenInputRef}
              name             = {fieldName}
              required         = {required}
              value            = {selected[identifier] || ''}
              onChange         = {() => {}}
              {...inputData}
            />
          }

          { disabled
            ? null
            : resetIcon && selected[identifier]
              ? <div className="autocomplete__icon autocomplete__reset" onClick={reset}>
                  <FontAwesomeIcon icon="times"/>
                </div>
              : feed
                ? <div className="autocomplete__icon">
                    <FontAwesomeIcon icon="search"/>
                  </div>
                : <div className="autocomplete__icon" onClick={toggle}>
                    <FontAwesomeIcon icon="caret-down"/>
                  </div>
          }

          {/* No match: keep the list visible so the user knows the search ran */}
          { active && suggestions.length === 0 && search &&
            <ul className="autocomplete__suggestions">
              <li className="autocomplete__suggestions__items autocomplete__suggestions__items--empty">
                {I18n.t("navigation.no_results")}
              </li>
            </ul>
          }

          {/* Rendered suggestions */}
          { active && suggestions.length > 0 &&
            <ul className="autocomplete__suggestions" ref={suggestionsRef}>
              { suggestions.map((suggestion, index) => {
                  const isSelected    = suggestion[identifier] == selected[identifier]
                  const isHighlighted = suggestion[identifier] == highlighted[identifier]
                  const classNames    = ["autocomplete__suggestions__items"]
                  if (isSelected) {
                    classNames.push("autocomplete__suggestions__items--selected")
                  } else if (isHighlighted) {
                    classNames.push("autocomplete__suggestions__items--highlighted")
                  }

                  return(
                    <li
                      key         = {index}
                      onMouseDown = {e => clickItem(e, suggestion)}
                      className   = {classNames.join(" ")}
                    >
                      <span dangerouslySetInnerHTML={highlight(suggestion[display], search)}/>
                    </li>
                  )
                }
              )}
            </ul>
          }
        </div>
      </div>
    </>
  )
}

export default Combobox
